import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Layout } from "@/components/layout/Layout";
import SEO from "@/components/SEO.jsx";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  ArrowUpRight,
  MapPin,
  Globe,
  Search,
  Share2,
  Code,
  Wrench,
  HelpCircle,
  CheckCircle,
  ChevronDown,
  Layers,
} from "lucide-react";

const services = [
  {
    icon: Globe,
    title: "Website Development",
    description: "Custom websites built for speed, clarity, and conversion.",
    link: "/services/website-development",
  },
  {
    icon: Search,
    title: "SEO Services",
    description:
      "Search engine optimisation that helps the right clients in Coquitlam find you.",
    link: "/services/seo-services",
  },
  {
    icon: Share2,
    title: "Social Media Marketing",
    description:
      "Consistent content and strategy that help your brand show up and build trust.",
    link: "/services/social-media-marketing",
  },
  {
    icon: Code,
    title: "Custom Tool Creation",
    description:
      "Dashboards, calculators, and custom web applications built around your workflow.",
    link: "/services/custom-tool-creation",
  },
  {
    icon: Wrench,
    title: "Maintenance & Support",
    description:
      "Ongoing updates, fixes, and technical support to keep everything performing well.",
    link: "/services/maintenance-support",
  },
  {
    icon: HelpCircle,
    title: "Not Sure What You Need?",
    description:
      "Tell us the problem. We will help you find the right digital solution.",
    link: "/contact",
  },
];

const leftServices = [services[0], services[3], services[4]];
const rightServices = [services[1], services[2], services[5]];

const servicesContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.07,
    },
  },
};

const serviceItemVariants = {
  hidden: { opacity: 0, y: 18 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: "easeOut",
    },
  },
};

const OrbitDot = ({ className = "" }) => {
  return (
    <span
      className={`absolute z-20 h-2.5 w-2.5 rounded-full bg-primary shadow-[0_0_20px_rgba(124,58,237,0.55)] ${className}`}
    />
  );
};

const OrbitCenter = () => {
  return (
    <motion.div
      variants={serviceItemVariants}
      className="relative mx-auto flex h-[240px] w-[240px] items-center justify-center sm:h-[270px] sm:w-[270px] lg:h-[285px] lg:w-[285px]"
    >
      <div className="absolute inset-8 rounded-full bg-primary/10 blur-3xl" />

      <div className="absolute inset-0 rounded-full border border-dashed border-primary/20" />
      <div className="absolute inset-5 rounded-full border border-primary/20" />
      <div className="absolute inset-10 rounded-full border border-primary/10" />
      <div className="absolute inset-[58px] rounded-full border border-primary/10" />

      <div className="absolute inset-[46px] rounded-full bg-primary/[0.06] blur-xl" />

      <OrbitDot className="left-1/2 top-[-5px] -translate-x-1/2" />
      <OrbitDot className="bottom-[-5px] left-1/2 -translate-x-1/2" />
      <OrbitDot className="left-[7%] top-[37%]" />
      <OrbitDot className="right-[7%] top-[37%]" />
      <OrbitDot className="bottom-[24%] left-[14%]" />
      <OrbitDot className="bottom-[24%] right-[14%]" />

      <div className="relative z-10 flex h-[170px] w-[170px] items-center justify-center rounded-full border border-primary/15 bg-background/95 px-5 text-center shadow-[0_18px_60px_rgba(124,58,237,0.16)] backdrop-blur-xl sm:h-[190px] sm:w-[190px] lg:h-[205px] lg:w-[205px]">
        <h2 className="font-display text-[19px] font-bold leading-[1.22] text-foreground sm:text-[21px] lg:text-[23px]">
          Digital Services for Coquitlam Businesses
        </h2>
      </div>
    </motion.div>
  );
};

const ServiceItem = ({ service }) => {
  const Icon = service.icon;

  return (
    <motion.div variants={serviceItemVariants}>
      <Link
        to={service.link}
        className="group relative flex gap-3 rounded-[22px] border border-transparent bg-background/45 p-3.5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/15 hover:bg-background/90 hover:shadow-[0_14px_36px_rgba(15,23,42,0.08)] sm:p-4"
      >
        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-primary/15 bg-background shadow-[0_8px_26px_rgba(124,58,237,0.12)] transition-all duration-300 group-hover:scale-105 group-hover:border-primary/35 group-hover:bg-primary/10 sm:h-12 sm:w-12">
          <Icon className="h-5 w-5 text-primary" />
        </div>

        <div className="min-w-0 flex-1">
          <h3 className="font-display text-[15px] font-semibold leading-snug text-foreground transition-colors duration-300 group-hover:text-primary sm:text-base">
            {service.title}
          </h3>

          <p className="mt-1.5 text-[13px] leading-relaxed text-muted-foreground sm:text-sm">
            {service.description}
          </p>

          <div className="mt-2 inline-flex items-center gap-2 text-[13px] font-semibold text-primary transition-all duration-300 group-hover:gap-3 sm:text-sm">
            <span>Learn More</span>
            <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </div>
        </div>
      </Link>
    </motion.div>
  );
};

const whyPoints = [
  "Industry research before we build anything",
  "Strategy before execution — always",
  "Custom solutions — not templates",
  "Ongoing support after launch",
];

const projects = [
  {
    title: "Punjab Financial & Insurance Services",
    description:
      "An insurance brand needed a complete digital foundation built from scratch. We built the full content strategy — brand voice, content pillars, and posting schedule across four platforms.",
    link: "/services/social-media-marketing",
  },
  {
    title: "Canada RDSP + Punjab RDSP",
    description:
      "Insurance agents had no tool to calculate RDSP grants and bonds for their clients. We built two custom branded calculators that made the process faster and clearer for agents and their clients.",
    link: "/services/custom-tool-creation",
  },
  {
    title: "Big Boys Motor Club",
    description:
      "A car brand was spending hours updating listings manually. We built a custom admin dashboard so they could manage inventory and post listings themselves — no developer needed.",
    link: "/services/custom-tool-creation",
  },
];

const faqs = [
  {
    question: "Do you work with businesses in Coquitlam?",
    answer:
      "Yes. We work with businesses across Coquitlam and the Lower Mainland. Most of our work is done remotely so location is not a barrier.",
  },
  {
    question: "What services do you offer Coquitlam businesses?",
    answer:
      "Website development, SEO services, social media marketing, custom tool creation, and maintenance and support. Most clients use a combination depending on what their business actually needs.",
  },
  {
    question:
      "How quickly can Square Trigger start working with a Coquitlam business?",
    answer:
      "We can usually start with a discovery call within a few days of first contact. From there the timeline depends on the scope of what needs to be built. We provide a clear plan and timeline after the discovery stage.",
  },
  {
    question: "How do I get started?",
    answer:
      "Book a free consultation through our contact page. We will talk about your business, what is not working online, and what the right next step looks like.",
  },
];
const HERO_IMAGE = "/coquitlam.webp";

export default function CoquitlamDigitalAgencyPage() {
  const [openFaq, setOpenFaq] = useState(null);

  return (
    <Layout>
      <SEO
  title="Digital Marketing Agency Coquitlam BC | Square Trigger"
  description="Square Trigger is a Digital marketing agency in Coquitlam, BC. We build Websites, SEO strategies, social media advertising, and custom tools for businesses."
  ogTitle="Digital Marketing Agency Coquitlam BC | Square Trigger"
  ogDescription="Square Trigger is a Digital marketing agency in Coquitlam, BC. We build Websites, SEO strategies, social media advertising, and custom tools for businesses."
  ogSiteName="Square Trigger Coquitlam"
  ogImage="https://squaretrigger.com/logo3.webp"
/>

      {/* Hero Section */}
      <section className="relative overflow-hidden">
  <div className="absolute inset-0">
    <div
      className="absolute inset-0 bg-cover bg-center"
      style={{ backgroundImage: `url(${HERO_IMAGE})` }}
    />
  </div>

        <div className="container-custom relative z-10 px-5 pt-[20px] pb-[44px] sm:px-5 sm:pt-[90px] sm:pb-[56px] lg:pt-[60px] lg:pb-[88px]">
          <div className="grid items-center gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mb-5 flex justify-center sm:mb-2 lg:justify-start"
              >
                <Badge className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/20 px-3 py-2 text-primary-foreground sm:px-4">
                  <MapPin className="h-4 w-4" />
                  <span className="text-sm font-medium">
                    Digital Agency in Coquitlam, BC
                  </span>
                </Badge>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                className='mx-auto m-0 mb-[18px] max-w-[950px] font-["Lato",system-ui,sans-serif] text-[clamp(2.2rem,4vw,3rem)] font-extrabold leading-[1.15] text-background lg:mx-0'
              >
                Digital Agency in Coquitlam That Builds What Your Business{" "}
                <span className="gradient-text">Actually Needs Online</span>
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mx-auto m-0 mb-6 max-w-[760px] text-[1.08rem] font-medium leading-[1.7] text-white/85 backdrop-blur-[2px] drop-shadow-[0_2px_8px_rgba(0,0,0,0.45)] lg:mx-0"
              >
                Websites. SEO. Social media. Custom tools. Built around your
                business goals — not a generic package. Serving Coquitlam and
                the surrounding Lower Mainland.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="flex flex-col flex-wrap items-stretch justify-center gap-[14px] sm:flex-row sm:items-center lg:justify-start"
              >
                <Button
                  variant="gradient"
                  asChild
                  className="group inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] px-7 py-[14px] text-[1rem] font-bold sm:w-auto"
                >
                  <Link to="/contact" className="flex items-center gap-2">
                    Book a Free Consultation
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Link>
                </Button>

                <Button
                  variant="glass"
                  asChild
                  className="inline-flex min-h-[52px] w-full items-center justify-center rounded-[10px] border border-white/20 bg-white/10 px-7 py-[14px] text-[1rem] font-bold text-white transition-all duration-300 hover:-translate-y-[2px] hover:bg-white/10 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] sm:w-auto"
                >
                  <Link to="/work">See Our Work</Link>
                </Button>
              </motion.div>
                     </div>

            <div className="hidden lg:flex items-center justify-center min-h-[420px]">
              <div className="h-[420px] w-full max-w-[520px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Who We Serve */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-20 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Serving Coquitlam
            </span>

            <h2 className="mt-3 mb-5 text-3xl font-display font-bold text-foreground md:text-4xl">
              We Work With Coquitlam Businesses That Need a{" "}
              <span className="gradient-text">Stronger Online Presence</span>
            </h2>

            <div className="rounded-2xl border border-border bg-card/60 p-6 shadow-soft backdrop-blur sm:p-8">
              <p className="mx-auto max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Coquitlam is one of the fastest-growing cities in the Lower
                Mainland — and with that growth comes increasing competition for
                local search visibility. Independent businesses, service
                providers, insurance brokers, automotive brands, and
                professional services across Coquitlam are all competing for the
                same online attention. Square Trigger works with Coquitlam
                businesses that need to show up more clearly in search, convert
                more website visitors into enquiries, and build a consistent
                digital presence that supports real business growth. We do not
                lead with packages — we start by understanding what the business
                actually needs, then build the right solution around that.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services */}
<section className="relative overflow-hidden bg-muted/30 py-8 sm:py-10 lg:py-10">
  <div className="pointer-events-none absolute inset-0">
    <div className="absolute left-[-90px] top-12 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />
    <div className="absolute bottom-0 right-[-100px] h-[360px] w-[360px] rounded-full bg-secondary/10 blur-3xl" />
    <div className="absolute left-1/2 top-1/2 h-[460px] w-[460px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-background/55 blur-3xl" />
  </div>

  <div className="container-custom relative z-10">
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.55 }}
      className="mx-auto mb-7 max-w-4xl text-center sm:mb-8 lg:mb-8"
    >
      <span className="text-sm font-medium uppercase tracking-wider text-primary">
        What We Do
      </span>

      <h2 className="mt-3 font-display text-3xl font-bold leading-tight text-foreground sm:text-4xl lg:text-[2.65rem]">
        Digital Services for{" "}
        <span className="gradient-text">Coquitlam Businesses</span>
      </h2>

      <p className="mx-auto mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground sm:text-base">
        We help Coquitlam businesses improve visibility, strengthen trust, and
        solve digital problems with practical services built around real goals.
      </p>
    </motion.div>

    <motion.div
      variants={servicesContainerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="hidden items-center gap-5 lg:grid lg:grid-cols-[minmax(0,1fr)_285px_minmax(0,1fr)] xl:gap-7"
    >
      <div className="flex flex-col gap-3.5">
        {leftServices.map((service) => (
          <ServiceItem key={service.title} service={service} />
        ))}
      </div>

      <OrbitCenter />

      <div className="flex flex-col gap-3.5">
        {rightServices.map((service) => (
          <ServiceItem key={service.title} service={service} />
        ))}
      </div>
    </motion.div>

    <motion.div
      variants={servicesContainerVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
      className="lg:hidden"
    >
      <OrbitCenter />

      <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-2">
        {services.map((service) => (
          <ServiceItem key={service.title} service={service} />
        ))}
      </div>
    </motion.div>
  </div>
</section>

      {/* Why Square Trigger */}
      <section className="section-padding relative overflow-hidden bg-foreground text-background">
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute top-10 left-10 h-80 w-80 rounded-full bg-primary/10 blur-3xl" />
          <div className="absolute right-10 bottom-10 h-80 w-80 rounded-full bg-secondary/10 blur-3xl" />
        </div>

        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-secondary">
              Why Choose Us
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold md:text-4xl">
              We Start With Your Problem. Then We Build the{" "}
              <span className="gradient-text">Right Solution.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] leading-relaxed text-background/70">
              Coquitlam businesses are competing in one of the most active
              digital markets in the Lower Mainland. We do not come in with a
              standard package. We start by understanding your business, your
              clients, and what is stopping you from growing online — then we
              build the right solution around that.
            </p>
          </motion.div>

          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {whyPoints.map((point, index) => (
              <motion.div
                key={point}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="rounded-2xl border border-background/10 bg-background/5 p-6 text-center transition-colors hover:border-primary/30 sm:text-left"
              >
                <CheckCircle className="mx-auto mb-4 h-6 w-6 text-primary sm:mx-0" />
                <p className="leading-relaxed text-background/75">{point}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Proof */}
      <section className="relative overflow-hidden bg-background pt-12 pb-6 sm:pt-14 sm:pb-8 lg:pt-16 lg:pb-10">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-12 text-center"
          >
            <span className="text-sm font-medium uppercase tracking-wider text-primary">
              Our Work
            </span>

            <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
              Real Projects.{" "}
              <span className="gradient-text">Real Problems Solved.</span>
            </h2>

            <p className="mx-auto mt-4 max-w-[900px] text-base leading-relaxed text-muted-foreground sm:text-lg">
              Every project below started with a specific business problem and a
              practical solution built around it. We bring the same
              problem-first approach to every Coquitlam business we work with.
            </p>
          </motion.div>

          <div className="grid gap-6 lg:grid-cols-3 lg:gap-8">
            {projects.map((project, index) => (
              <motion.div
                key={project.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.08 }}
                className="h-full"
              >
                <Card className="card-hover flex h-full flex-col border-border p-6 text-center transition-all duration-300 hover:border-primary/30 sm:text-left">
                  <div className="mx-auto mb-4 flex h-10 w-10 items-center justify-center rounded-xl gradient-primary-soft sm:mx-0">
                    <Layers className="h-5 w-5 text-primary" />
                  </div>

                  <h3 className="mb-3 text-lg font-display font-semibold text-foreground sm:text-xl">
                    {project.title}
                  </h3>

                  <p className="mb-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
                    {project.description}
                  </p>

                  <Link
                    to={project.link}
                    className="mt-auto inline-flex items-center justify-center gap-2 text-primary hover:underline sm:justify-start"
                  >
                    View Service
                    <ArrowRight className="h-4 w-4" />
                  </Link>
                </Card>
              </motion.div>
            ))}
          </div>

          <div className="mt-10 text-center">
            <Button variant="gradient" size="lg" asChild>
              <Link to="/work" className="inline-flex items-center gap-2">
                See Our Work
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <div className="rounded-[28px] border border-border/70 bg-card/40 px-5 py-10 shadow-soft backdrop-blur-sm sm:px-8 sm:py-12 lg:px-12 lg:py-14">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mb-10 text-center sm:mb-12"
            >
              <h2 className="mt-3 text-3xl font-display font-bold text-foreground md:text-4xl">
                What Coquitlam Businesses{" "}
                <span className="gradient-text">Usually Ask First</span>
              </h2>
            </motion.div>

            <div className="mx-auto max-w-[1000px] space-y-4">
              {faqs.map((faq, index) => (
                <motion.div
                  key={faq.question}
                  className="relative z-10 overflow-hidden rounded-xl border border-border bg-background"
                >
                  <button
                    type="button"
                    onPointerUp={() =>
                      setOpenFaq(openFaq === index ? null : index)
                    }
                    className="pointer-events-auto flex w-full touch-manipulation items-center justify-between p-5 text-left transition-colors hover:bg-accent/50 sm:p-6"
                  >
                    <span className="pr-6 font-semibold text-foreground">
                      {faq.question}
                    </span>

                    <ChevronDown
                      className={`h-5 w-5 flex-shrink-0 text-muted-foreground transition-transform duration-300 ${
                        openFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <div
                    className={`grid transition-[grid-template-rows] duration-300 ease-out ${
                      openFaq === index
                        ? "grid-rows-[1fr]"
                        : "grid-rows-[0fr]"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <div className="px-5 pb-5 leading-relaxed text-muted-foreground sm:px-6 sm:pb-6">
                        {faq.answer}
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: faqs.map((faq) => ({
                  "@type": "Question",
                  name: faq.question,
                  acceptedAnswer: {
                    "@type": "Answer",
                    text: faq.answer,
                  },
                })),
              })}
            </script>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative overflow-hidden bg-background py-12 sm:py-14 lg:py-16">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[28px] border border-border/70 bg-card/40 px-6 py-14 text-center shadow-soft backdrop-blur-sm sm:px-10 sm:py-16 lg:px-16 lg:py-18"
          >
            <div className="mx-auto max-w-[1000px]">
              <h2 className="mb-4 text-3xl font-display font-bold text-foreground sm:mb-6 md:text-4xl">
                Your Coquitlam Business Has a Digital Problem. We Build the
                Solution.
              </h2>

              <p className="mx-auto mb-8 max-w-[860px] text-base leading-relaxed text-muted-foreground sm:text-lg">
                Let us talk about what your business actually needs online.
              </p>

              <Button
                variant="gradient"
                size="lg"
                asChild
                className="group w-full justify-center sm:w-auto"
              >
                <Link
                  to="/contact"
                  className="flex items-center justify-center gap-2"
                >
                  Book a Free Consultation
                  <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </Layout>
  );
}